"use client";

import { Mail } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const preparationItems = [{
  id: "01",
  name: "Bring Your Medication List",
  description: "Please bring an updated list of all medications, including inhalers, nebulizer treatments, oxygen use, CPAP equipment, and any over-the-counter medications or supplements."
}, {
  id: "02",
  name: "Bring Relevant Records",
  description: "If available, bring recent chest X-ray or CT scan reports, pulmonary function testing results, sleep study records, CPAP information, hospital discharge paperwork, or notes from other physicians."
}, {
  id: "03",
  name: "Insurance & Identification",
  description: "Please bring your insurance card, photo ID, and any referral information required by your insurance plan."
}, {
  id: "04",
  name: "Questions for the Doctor",
  description: "Write down any breathing concerns, sleep issues, symptoms, medication questions, or follow-up concerns you want to discuss during the visit."
}];
export default function PreparingForYourVisit() {
  const header = useRevealOnScroll<HTMLDivElement>({
    once: true,
    threshold: 0.2
  });
  const callout = useRevealOnScroll<HTMLDivElement>({
    once: true,
    threshold: 0.2
  });
  return <section data-section-id="459" id="preparing-for-your-visit" className="bg-background text-foreground relative overflow-hidden py-20 md:py-28" style={{
    backgroundImage: "radial-gradient(circle at 20% 30%, hsl(174 25% 45% / 0.03) 1px, transparent 1px), radial-gradient(circle at 80% 70%, hsl(174 25% 45% / 0.02) 1px, transparent 1px)",
    backgroundSize: "40px 40px, 60px 60px"
  }}>
      <div className="mx-auto max-w-7xl px-6 md:px-10">
        <div ref={header.ref} className={`grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 mb-16 md:mb-24 transition-all duration-700 ${header.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="md:col-span-7">
            <p className="font-mono text-xs md:text-sm uppercase tracking-widest mb-6" style={{
            color: "var(--ring)"
          }}>
              PATIENT INFORMATION
            </p>
            <h2 className="font-serif font-default text-5xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tight text-foreground">
              Preparing for Your Visit
            </h2>
          </div>
          <div className="md:col-span-5 md:pt-8 flex md:items-end">
            <p className="font-default text-base md:text-lg leading-relaxed text-slate-text max-w-[480px]">
              Before your appointment, please bring your current medication list, insurance information, and any recent test results, imaging reports, sleep study records, or hospital discharge paperwork that may be relevant to your pulmonary or sleep medicine care.
            </p>
          </div>
        </div>

        <div className="border-t border-foreground/10">
          {preparationItems.map((item, i) => {
          const row = useRevealOnScroll<HTMLDivElement>({
            once: true,
            threshold: 0.15
          });
          return <div key={item.id} data-index={i} ref={row.ref} className={`relative transition-all duration-700 border-b border-foreground/10 ${row.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
            transitionDelay: `${i * 80}ms`
          }}>
                <div className="grid grid-cols-12 gap-4 md:gap-6 py-10 md:py-14 items-start md:items-center">
                  <div className="col-span-3 md:col-span-2">
                    <div className="font-serif font-default text-6xl md:text-8xl lg:text-9xl leading-none text-foreground" style={{
                  WebkitTextStroke: "1px currentColor",
                  color: "transparent",
                  opacity: 0.2
                }}>
                      {item.id}
                    </div>
                  </div>

                  <div className="col-span-9 md:col-span-4">
                    <h3 className="font-serif font-default italic text-2xl md:text-3xl lg:text-4xl text-foreground leading-tight">
                      {item.name}
                    </h3>
                  </div>

                  <div className="col-span-12 md:col-span-6">
                    <p className="font-default text-sm md:text-[15px] leading-relaxed text-slate-text max-w-[600px]">
                      {item.description}
                    </p>
                  </div>
                </div>
              </div>;
        })}
        </div>

        <div ref={callout.ref} className={`mt-16 md:mt-24 transition-all duration-700 ${callout.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="border p-8 md:p-12 max-w-3xl mx-auto text-center rounded-md shadow-sm" style={{
          backgroundColor: "var(--secondary)",
          borderColor: "var(--border)",
          borderWidth: "1px"
        }}>
            <div className="flex justify-center mb-5">
              <div className="w-10 h-10 flex items-center justify-center rounded-md" style={{
              backgroundColor: "var(--primary)",
              color: "var(--primary-foreground)"
            }}>
                <Mail className="w-5 h-5" />
              </div>
            </div>
            <p className="font-serif font-default italic text-xl md:text-2xl leading-relaxed text-foreground">Have a question before your visit?&nbsp;<Link to="/ContactPage" className="underline underline-offset-4 transition-colors hover:opacity-80" style={{
              color: 'var(--ring)'
            }}>Contact our office</Link> — our team will help direct you to the right next step.</p>
            <div className="mt-6 flex justify-center">
              <Button asChild variant="outline" className="font-mono text-xs uppercase tracking-widest border-primary/30 hover:bg-primary hover:text-primary-foreground rounded-md">
                <Link to="/ContactPage">Get in touch</Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}